import React, { FC, useCallback, useEffect, useState } from 'react';
import { Filter } from 'react-ionicons';
import Metrics from './Metrics';
import { useQuery } from 'react-query';
import { getActiveOrdersGraph } from 'api/orders/api';
import EmptyActiveOrder from '../EmptyActiveOrders/EmptyState';
import Skeleton from 'components/Skeleton/Skeleton';
import FilterDropdown, {
  IFilterDropdownOption,
} from 'components/FilterDropdown';
import { ResponseActiveOrdersGraphType } from 'api/orders/schemas';

interface ActiveOrdersProps {
  onStatusChange: (status: string) => void;
}

const ActiveOrders: FC<ActiveOrdersProps> = ({ onStatusChange }) => {
  const [metrics, setMetrics] = useState<ResponseActiveOrdersGraphType | null>(
    null,
  );
  const [selected, setSelected] = useState<string[]>([]);

  const { data, isLoading } = useQuery(['active-orders-metrics'], () =>
    getActiveOrdersGraph(),
  );

  const onSelectAll = useCallback(() => {
    setSelected(data?.statuses.map(({ status }) => status) ?? []);
  }, [data]);

  useEffect(() => {
    if (data) {
      setMetrics(data);
      onSelectAll();
    }
  }, [data, onSelectAll]);

  const onSelectFilter = (item: IFilterDropdownOption) => {
    setSelected((prevSelected) => {
      const _selected = prevSelected.includes(item.value)
        ? prevSelected.filter((val) => val !== item.value)
        : [...prevSelected, item.value];

      const statuses =
        data?.statuses.filter(({ status }) => _selected.includes(status)) ?? [];

      const total = statuses.reduce((acc, { count }) => acc + count, 0);

      setMetrics({ statuses, total });
      return _selected;
    });
  };

  if (isLoading)
    return <Skeleton className="h-full min-h-[338px] w-full !rounded" />;

  if (!data || data.total === 0) {
    return <EmptyActiveOrder />;
  }

  return (
    <div className="group flex h-full w-full flex-col gap-8 rounded bg-background-base-surface-2 p-6 shadow-elevation3 hover:bg-background-base-surface-3">
      <div className="flex items-center justify-between  text-content-base-default">
        <p className="text-xl font-bold">Active orders</p>
        <FilterDropdown
          type="multi"
          label={<Filter color="currentColor" />}
          options={
            data?.statuses.map(({ status }) => ({
              label: status,
              value: status,
            })) ?? []
          }
          onSelect={onSelectFilter}
          selected={selected}
          showSelectAll={true}
        />
      </div>
      {metrics && (
        <Metrics
          metrics={metrics}
          onClick={(status) => onStatusChange(status)}
        />
      )}
    </div>
  );
};

export default ActiveOrders;
